import React, { createContext, useContext, useEffect, useState } from 'react';
import { jwtDecode } from 'jwt-decode';
import { api } from '../api/nocode';
import { computeTrailStartDate } from '../utils/campLogic';
import { createOwnedCampUser, ensureCampUserOwner } from '../utils/campUser';

const AuthContext = createContext();

function getTokenFromLocation() {
  const searchToken = new URLSearchParams(window.location.search).get('token');
  if (searchToken) return searchToken;

  const hashQuery = window.location.hash.split('?')[1];
  return hashQuery ? new URLSearchParams(hashQuery).get('token') : null;
}

function removeTokenFromLocation() {
  const hashPath = window.location.hash.split('?')[0];
  const searchParams = new URLSearchParams(window.location.search);
  searchParams.delete('token');
  const search = searchParams.toString() ? `?${searchParams}` : '';

  window.history.replaceState(
    null,
    '',
    `${window.location.pathname}${search}${hashPath}`
  );
}

function getHubIdentity(decoded) {
  const emailCandidates = [
    decoded?.email,
    decoded?.user_email,
    decoded?.user?.email,
    decoded?.data?.email,
    decoded?.profile?.email,
    decoded?.user_metadata?.email,
    decoded?.sub
  ];

  const nameCandidates = [
    decoded?.name,
    decoded?.full_name,
    decoded?.user?.name,
    decoded?.user?.full_name,
    decoded?.data?.name,
    decoded?.data?.full_name,
    decoded?.user_metadata?.name,
    decoded?.user_metadata?.full_name
  ];

  const email = emailCandidates.find(
    (value) => typeof value === 'string' && value.includes('@')
  );

  return {
    email: email?.trim().toLowerCase() ?? '',
    name: nameCandidates.find(
      (value) => typeof value === 'string' && value.trim()
    ) ?? 'Camper'
  };
}

function getRows(response) {
  return Array.isArray(response) ? response : (response?.data ?? []);
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      try {
        let token = getTokenFromLocation();

        if (token) {
          localStorage.setItem('gwa_token', token);
          removeTokenFromLocation();
        } else {
          token = localStorage.getItem('gwa_token');
        }

        if (token === 'master_admin_token') {
          setUser({
            id: 'master-admin',
            name: 'Master Admin',
            email: 'team@gospelwiseauthor.com',
            role: 'admin',
            pass_type: 'allaccess',
            owner_id: 'master-admin'
          });
          return;
        }

        if (!token) return;

        const decoded = jwtDecode(token);
        const { email, name } = getHubIdentity(decoded);

        if (!email) {
          throw new Error('The Hub token did not include an email address.');
        }

        const appData = decoded?.gwacamp ?? decoded?.apps?.gwacamp ?? {};
        const fallbackRole = email === 'team@gospelwiseauthor.com' ? 'admin' : 'camper';
        const tokenRole = appData?.role ?? decoded?.role ?? fallbackRole;
        const tokenPassType = appData?.pass_type ?? decoded?.pass_type ?? 'free';
        const usersResponse = await api.get('users_gwacamp');
        const matchedUser = getRows(usersResponse).find(
          (row) => (row?.email ?? '').trim().toLowerCase() === email
        );

        if (matchedUser) {
          let resolvedUser = matchedUser;

          try {
            resolvedUser = await ensureCampUserOwner(matchedUser);
          } catch (ownershipError) {
            console.warn('Camp ownership repair will retry on the next visit.', ownershipError);
          }

          const updates = {};

          if (tokenRole === 'admin' && resolvedUser?.role !== 'admin') {
            updates.role = 'admin';
          }

          if (tokenPassType === 'allaccess' && resolvedUser?.pass_type !== 'allaccess') {
            updates.pass_type = 'allaccess';
          }

          if (
            ['evergreen', 'evergreen-aap'].includes(resolvedUser?.pass_type)
            && !resolvedUser?.trail_start_date
          ) {
            updates.trail_start_date = computeTrailStartDate();
          }

          if (Object.keys(updates).length > 0) {
            await api.put('users_gwacamp', resolvedUser.id, updates);
            resolvedUser = { ...resolvedUser, ...updates };
          }

          setUser(resolvedUser);
          return;
        }

        const trailStart = ['evergreen', 'evergreen-aap'].includes(tokenPassType)
          ? computeTrailStartDate()
          : null;

        const createdUser = await createOwnedCampUser({
          name,
          email,
          role: tokenRole,
          pass_type: tokenPassType,
          trail_start_date: trailStart,
          first_login_complete: 0
        }, email);

        setUser(createdUser);
      } catch (error) {
        localStorage.removeItem('gwa_token');
        console.error('Hub authentication error', error);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, []);

  const adminLogin = (email, password) => {
    if (email === 'team@gospelwiseauthor.com' && password === 'ZoBoWriMo8954*') {
      const adminUser = {
        id: 'master-admin',
        name: 'Master Admin',
        email: 'team@gospelwiseauthor.com',
        role: 'admin',
        pass_type: 'allaccess',
        owner_id: 'master-admin'
      };

      setUser(adminUser);
      localStorage.setItem('gwa_token', 'master_admin_token');
      return true;
    }

    return false;
  };

  const logout = () => {
    localStorage.removeItem('gwa_token');
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, adminLogin, logout, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);